export type InstitutionOverageBand = {
  from?: number | null;
  to?: number | null;
  min?: number | null;
  max?: number | null;
  start?: number | null;
  end?: number | null;
  starting_member?: number | null;
  ending_member?: number | null;
  amount_cents?: number | null;
  unit_amount_cents?: number | null;
  price_per_member_cents?: number | null;
  monthly_amount_cents?: number | null;
  overage_cents?: number | null;
  amount?: number | null;
  price_per_member?: number | null;
  contact_required?: boolean | null;
};

export type InstitutionPlanMetadata = {
  usage_based?: boolean | null;
  requires_contact?: boolean | null;
  included_members?: number | null;
  overage_bands?: InstitutionOverageBand[] | null;
};

export type InstitutionOverageBreakdownLine = {
  from: number;
  to: number | null;
  count: number;
  amount_cents: number;
  line_total_cents: number;
};

const asNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const next = Number(value);
  return Number.isFinite(next) ? next : null;
};

const bandRateCents = (band: InstitutionOverageBand): number => {
  const cents =
    asNumber(band.amount_cents) ??
    asNumber(band.unit_amount_cents) ??
    asNumber(band.price_per_member_cents) ??
    asNumber(band.monthly_amount_cents) ??
    asNumber(band.overage_cents);
  if (cents !== null) return cents;

  const euros = asNumber(band.amount) ?? asNumber(band.price_per_member);
  return euros !== null ? Math.round(euros * 100) : 0;
};

const bandStart = (band: InstitutionOverageBand): number =>
  asNumber(band.from) ?? asNumber(band.min) ?? asNumber(band.start) ?? asNumber(band.starting_member) ?? 1;

const bandEnd = (band: InstitutionOverageBand): number | null =>
  asNumber(band.to) ?? asNumber(band.max) ?? asNumber(band.end) ?? asNumber(band.ending_member);

const chargeableBandRange = (
  band: InstitutionOverageBand,
  includedMembers: number,
  activeMembers: number,
) => {
  const start = Math.max(1, Math.floor(bandStart(band)));
  const endValue = bandEnd(band);
  const end = endValue === null ? activeMembers : Math.max(start, Math.floor(endValue));
  const chargedStart = Math.max(start, includedMembers + 1);
  const chargedEnd = Math.min(end, activeMembers);
  const count = Math.max(0, chargedEnd - chargedStart + 1);

  return { chargedStart, chargedEnd, count };
};

export const institutionPriceRequiresContact = (
  planMetadata: InstitutionPlanMetadata,
  activeMemberCount: number,
): boolean => {
  if (!planMetadata?.usage_based) return !!planMetadata?.requires_contact;
  if (planMetadata.requires_contact) return true;

  const includedMembers = Math.max(0, asNumber(planMetadata.included_members) ?? 0);
  const activeMembers = Math.max(0, Math.floor(asNumber(activeMemberCount) ?? 0));
  const extraMembers = Math.max(0, activeMembers - includedMembers);
  if (extraMembers <= 0) return false;

  const bands = Array.isArray(planMetadata.overage_bands) ? planMetadata.overage_bands : [];
  return bands.some((band) => band.contact_required === true && chargeableBandRange(band, includedMembers, activeMembers).count > 0);
};

export const calculateInstitutionOverage = (
  planMetadata: InstitutionPlanMetadata,
  activeMemberCount: number,
): number => {
  if (!planMetadata?.usage_based) return 0;

  const includedMembers = Math.max(0, asNumber(planMetadata.included_members) ?? 0);
  const activeMembers = Math.max(0, Math.floor(asNumber(activeMemberCount) ?? 0));
  const extraMembers = Math.max(0, activeMembers - includedMembers);
  if (extraMembers <= 0) return 0;

  const bands = Array.isArray(planMetadata.overage_bands) ? planMetadata.overage_bands : [];
  return bands.reduce((total, band) => {
    const rateCents = bandRateCents(band);
    if (band.contact_required === true) return total;
    if (rateCents <= 0) return total;

    const { count } = chargeableBandRange(band, includedMembers, activeMembers);
    return total + count * rateCents;
  }, 0);
};

export const calculateInstitutionOverageBreakdown = (
  planMetadata: InstitutionPlanMetadata,
  activeMemberCount: number,
): InstitutionOverageBreakdownLine[] => {
  if (!planMetadata?.usage_based) return [];

  const includedMembers = Math.max(0, asNumber(planMetadata.included_members) ?? 0);
  const activeMembers = Math.max(0, Math.floor(asNumber(activeMemberCount) ?? 0));
  const extraMembers = Math.max(0, activeMembers - includedMembers);
  if (extraMembers <= 0) return [];

  const bands = Array.isArray(planMetadata.overage_bands) ? planMetadata.overage_bands : [];
  return bands.flatMap((band) => {
    const amountCents = bandRateCents(band);
    if (band.contact_required === true) return [];
    if (amountCents <= 0) return [];

    const { chargedStart, chargedEnd, count } = chargeableBandRange(band, includedMembers, activeMembers);
    if (count <= 0) return [];
    return [{
      from: chargedStart,
      to: chargedEnd,
      count,
      amount_cents: amountCents,
      line_total_cents: count * amountCents,
    }];
  });
};

export const calculateInstitutionMonthlyPrice = (
  planMetadata: InstitutionPlanMetadata,
  activeMemberCount: number,
  baseAmountCents: number,
): number => {
  const base = Math.max(0, Math.floor(asNumber(baseAmountCents) ?? 0));
  if (!planMetadata?.usage_based) return base;
  return base + calculateInstitutionOverage(planMetadata, activeMemberCount);
};

export const formatInstitutionPrice = (amountCents: number): string => {
  const amount = Math.max(0, Math.floor(asNumber(amountCents) ?? 0)) / 100;
  return new Intl.NumberFormat("en-IE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const formatInstitutionEffectiveRate = (totalCents: number, memberCount: number): string => {
  const divisor = Math.max(1, Math.floor(asNumber(memberCount) ?? 1));
  const exactCents = Math.max(0, asNumber(totalCents) ?? 0) / divisor;
  const roundedCents = Math.round(exactCents);
  const approximation = Math.abs(exactCents - roundedCents) > 0.000001 ? "~" : "";
  return `${approximation}${formatInstitutionPrice(roundedCents)}`;
};
